import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, ChevronDown, ListPlus, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmt, todayStr } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_CLS = {
  "Not Started": "bg-slate-100 text-slate-700",
  Started: "bg-amber-100 text-amber-900",
  Finished: "bg-emerald-100 text-emerald-900",
  "N/A": "bg-slate-200 text-slate-500",
};
const isDone = (i) => ["Finished", "N/A"].includes(i.status);
const isOverdue = (i) => !isDone(i) && !!i.effective_due && i.effective_due < todayStr();
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export const Progress = ({ done, total, testid }) => {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <div data-testid={testid}>
      <div className="flex justify-between text-xs text-slate-500 mb-1"><span>{done} of {total} done</span><span className="font-bold text-[#24474A]">{pct}%</span></div>
      <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-[#24474A] transition-all duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
};

const DueCell = ({ item, save }) => {
  const overdue = isOverdue(item);
  return (
    <div className="flex flex-col">
      <Input type="date" value={item.effective_due || ""} onChange={(e) => save({ due_date: e.target.value })}
        className={`h-8 text-xs ${overdue ? "border-rose-400 text-rose-700 bg-rose-50 font-bold" : ""}`} data-testid={`checklist-due-${item.id}`} />
      {overdue && <span className="text-[10px] text-rose-600 font-bold mt-0.5 flex items-center gap-1" data-testid={`checklist-overdue-${item.id}`}><AlertTriangle className="w-3 h-3" /> Overdue</span>}
      {!item.due_date && item.effective_due && !overdue && <span className="text-[10px] text-slate-400 mt-0.5">{Math.abs(item.days_before)} day{Math.abs(item.days_before) === 1 ? "" : "s"} {item.days_before < 0 ? "after" : "before"} opening</span>}
    </div>
  );
};

const TaskCell = ({ item, pid, onConverted }) => {
  const [busy, setBusy] = useState(false);
  if (item.task_id) {
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700" data-testid={`checklist-task-linked-${item.id}`}><CheckCircle2 className="w-4 h-4" /> Task {item.task_status === "done" ? "done" : "created"}</span>;
  }
  const convert = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${pid}/checklist/${item.id}/task`);
      toast.success(data.due ? `Task created for ${data.assignee_name}, due ${fmt(data.due, "d MMM")}` : `Task created for ${data.assignee_name} (no due date, so not on the calendar)`);
      onConverted();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return <Button size="sm" variant="outline" className="h-8 rounded-full text-xs" disabled={busy} onClick={convert} data-testid={`checklist-to-task-${item.id}`}><ListPlus className="w-3.5 h-3.5 mr-1" /> Make task</Button>;
};

const gridCols = (fl) => [fl.convert_to_task && "20px", "1.3fr", fl.status && "140px", fl.owner && "170px", fl.due_date && "150px", fl.notes && "1.4fr", fl.convert_to_task && "120px"].filter(Boolean).join("_");

const Row = ({ item, users, pid, fields, onSaved, onConverted, selected, onToggle }) => {
  const [notes, setNotes] = useState(item.notes || "");
  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/projects/${pid}/checklist/${item.id}`, patch);
      onSaved(data);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className={`grid grid-cols-1 gap-2 md:items-center px-4 py-2.5 border-t border-slate-100 ${isOverdue(item) ? "bg-rose-50/60" : ""}`}
      style={{ "--cols": gridCols(fields).replace(/_/g, " ") }} data-testid={`checklist-row-${item.id}`}>
      {fields.convert_to_task && (
        <input type="checkbox" className="w-4 h-4 accent-[#24474A] hidden md:block" disabled={!!item.task_id} checked={selected} onChange={onToggle} data-testid={`checklist-select-${item.id}`} />
      )}
      <div className={`text-sm font-bold ${item.status === "Finished" ? "text-slate-400 line-through" : isOverdue(item) ? "text-rose-700" : "text-slate-800"}`}>{item.item}</div>
      {fields.status && (
        <Select value={item.status} onValueChange={(v) => save({ status: v })}>
          <SelectTrigger className={`h-8 rounded-full text-xs border-0 font-bold ${STATUS_CLS[item.status]}`} data-testid={`checklist-status-${item.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.keys(STATUS_CLS).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      )}
      {fields.owner && (
        <Select value={item.owner_id || "none"} onValueChange={(v) => save({ owner_id: v === "none" ? null : v })}>
          <SelectTrigger className="h-8 rounded-full text-xs" data-testid={`checklist-owner-${item.id}`}><SelectValue placeholder="Owner" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">No owner</SelectItem>
            {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      )}
      {fields.due_date && <DueCell item={item} save={save} />}
      {fields.notes && (
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => notes !== (item.notes || "") && save({ notes })}
          placeholder="Notes, links…" className="h-8 text-xs" data-testid={`checklist-notes-${item.id}`} />
      )}
      {fields.convert_to_task && <TaskCell item={item} pid={pid} onConverted={onConverted} />}
    </div>
  );
};

export const ProjectChecklist = ({ project, onChanged, readOnly }) => {
  const [items, setItems] = useState(project.checklist);
  const [users, setUsers] = useState([]);
  const [closed, setClosed] = useState({});
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const fields = project.checklist_fields;

  useEffect(() => {
    setItems(project.checklist);
    setSel([]);
  }, [project.checklist]);

  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const bulk = async (ids) => {
    if (!ids.length) return toast.info("Every item here is already a task");
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${project.id}/checklist/tasks`, { item_ids: ids });
      toast.success(`${data.created} task(s) created${data.no_due ? ` · ${data.no_due} without a due date (not on the calendar)` : ""}`);
      onChanged();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data));
  }, []);

  const onSaved = (data) => setItems((list) => list.map((i) => (i.id === data.id ? data : i)));
  const sections = [...new Set(items.map((i) => i.section))];
  const overdue = items.filter(isOverdue).length;

  return (
    <section className="mb-6 checklist" data-testid="project-checklist">
      <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 mb-3">
        <div className="flex flex-wrap items-center gap-2 font-bold text-slate-800 mb-3">
          <ClipboardCheck className="w-5 h-5 text-[#24474A]" /> <span data-testid="checklist-name">{project.checklist_name}</span>
          {overdue > 0 && <span className="ml-auto text-xs font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2.5 py-1" data-testid="checklist-overdue-count">{overdue} overdue</span>}
        </div>
        <Progress done={items.filter(isDone).length} total={items.length} testid="checklist-progress" />
        {fields.convert_to_task && !readOnly && sel.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mt-3 rounded-xl bg-[#EAF2F2] px-3 py-2" data-testid="checklist-bulk-bar">
            <span className="text-sm font-bold text-[#24474A]">{sel.length} selected</span>
            <Button size="sm" className="rounded-full ml-auto" disabled={busy} onClick={() => bulk(sel)} data-testid="checklist-bulk-make-tasks-btn"><ListPlus className="w-4 h-4 mr-1" /> Make {sel.length} task(s)</Button>
            <Button size="icon" variant="ghost" onClick={() => setSel([])} data-testid="checklist-bulk-clear-btn"><X className="w-4 h-4" /></Button>
          </div>
        )}
      </div>
      <div className="space-y-3">
        {sections.map((s) => {
          const list = items.filter((i) => i.section === s);
          const late = list.filter(isOverdue).length;
          const open = list.filter((i) => !i.task_id).map((i) => i.id);
          return (
            <div key={s} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`checklist-section-${slug(s)}`}>
              <div className="flex items-center bg-slate-50">
              <button type="button" onClick={() => setClosed((c) => ({ ...c, [s]: !c[s] }))} className="flex-1 flex justify-between items-center px-4 py-2.5 hover:bg-slate-100 transition-colors" data-testid={`checklist-section-toggle-${slug(s)}`}>
                <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#24474A]">{s}</span>
                <span className="flex items-center gap-2 text-xs text-slate-500">
                  {late > 0 && <span className="text-rose-600 font-bold">{late} overdue</span>}
                  {list.filter(isDone).length}/{list.length}
                  <ChevronDown className={`w-4 h-4 transition-transform ${closed[s] ? "-rotate-90" : ""}`} />
                </span>
              </button>
              {fields.convert_to_task && !readOnly && open.length > 0 && (
                <button type="button" disabled={busy} onClick={() => window.confirm(`Make ${open.length} task(s) from "${s}"?`) && bulk(open)} className="shrink-0 mr-2 flex items-center gap-1 rounded-full border border-[#24474A]/30 bg-white px-2.5 py-1 text-xs font-bold text-[#24474A] hover:bg-[#EAF2F2] transition-colors" data-testid={`checklist-section-make-tasks-${slug(s)}`}>
                  <ListPlus className="w-3.5 h-3.5" /> <span className="hidden sm:inline">Make all tasks</span><span className="sm:hidden">All</span> ({open.length})
                </button>
              )}
              </div>
              {!closed[s] && <fieldset disabled={readOnly}>{list.map((i) => <Row key={i.id} item={i} users={users} pid={project.id} fields={fields} onSaved={onSaved} onConverted={onChanged} selected={sel.includes(i.id)} onToggle={() => toggle(i.id)} />)}</fieldset>}
            </div>
          );
        })}
      </div>
    </section>
  );
};
